type OwnerLike = {
  id?: string;
  uid?: string;
  displayName?: string;
  profileImageURL?: string;
  imageURL?: string;
  thumbnailURL?: string;
  businessReference?: { entityUID?: string };
};

function normalizeId(ownerId: string | number | null | undefined): string {
  return ownerId == null ? '' : String(ownerId).trim();
}

function namesMatch(left?: string | null, right?: string | null): boolean {
  if (!left || !right) {
    return false;
  }

  return left.trim().toLowerCase() === right.trim().toLowerCase();
}

export function getTagValue(tags: string[] | null | undefined, param: string): string | null {
  if (!tags?.length) {
    return null;
  }

  const prefix = `${param}:`;
  const tag = tags.find((item) => item.startsWith(prefix) || item.split(':')[0] === param);
  if (!tag) {
    return null;
  }

  return tag.slice(prefix.length) || null;
}

export function isSameOwner(ownerId: string | number | null | undefined, entity: OwnerLike | null | undefined): boolean {
  const id = normalizeId(ownerId);
  if (!id || !entity) {
    return false;
  }

  return normalizeId(entity.businessReference?.entityUID) === id || normalizeId(entity.id) === id;
}

export function findOwnerUser<T extends OwnerLike>(
  users: T[] | null | undefined,
  ownerId: string | number | null | undefined
): T | undefined {
  const id = normalizeId(ownerId);
  if (!id || !users?.length) {
    return undefined;
  }

  return users.find((user) => normalizeId(user.businessReference?.entityUID) === id);
}

export function findOwnerUserByAnyId<T extends OwnerLike>(
  users: T[] | null | undefined,
  ownerId: string | number | null | undefined
): T | undefined {
  const id = normalizeId(ownerId);
  if (!id || !users?.length) {
    return undefined;
  }

  return users.find(
    (user) =>
      normalizeId(user.businessReference?.entityUID) === id || normalizeId(user.uid) === id || normalizeId(user.id) === id
  );
}

export function pickProfileImage(entity?: OwnerLike | null): string | undefined {
  if (!entity) {
    return undefined;
  }

  return entity.profileImageURL || entity.imageURL || entity.thumbnailURL || undefined;
}

function isSamePerson(ownerId: string, entity: OwnerLike | null | undefined): boolean {
  if (!entity) {
    return false;
  }

  return (
    normalizeId(entity.businessReference?.entityUID) === ownerId ||
    normalizeId(entity.uid) === ownerId ||
    normalizeId(entity.id) === ownerId
  );
}

export function resolveOwner(
  ownerId: string | number | null | undefined,
  fallback: OwnerLike | null | undefined,
  sources: {
    users?: OwnerLike[] | null;
    extras?: Array<OwnerLike | null | undefined>;
    tagDisplayName?: string | null;
  } = {}
): OwnerLike | undefined {
  const id = normalizeId(ownerId) || normalizeId(fallback?.id);
  const displayNameHint = sources.tagDisplayName || fallback?.displayName;
  const namedUser = findOwnerUser(sources.users, id);
  const photoUser =
    findOwnerUserByAnyId(sources.users, id) || sources.users?.find((user) => namesMatch(user.displayName, displayNameHint));
  const extra =
    sources.extras?.find((entity) => isSamePerson(id, entity)) ||
    sources.extras?.find((entity) => namesMatch(entity?.displayName, displayNameHint));
  const displayName = sources.tagDisplayName || namedUser?.displayName || fallback?.displayName || extra?.displayName;

  if (!displayName && !id) {
    return undefined;
  }

  return {
    id: id || namedUser?.businessReference?.entityUID || fallback?.id,
    uid: photoUser?.uid || extra?.uid || fallback?.uid,
    displayName,
    profileImageURL: pickProfileImage(photoUser) || pickProfileImage(extra) || pickProfileImage(fallback),
    businessReference:
      namedUser?.businessReference ||
      photoUser?.businessReference ||
      extra?.businessReference ||
      fallback?.businessReference,
  };
}
